// Help Centre "My requests" — aggregates the customer's tickets across ALL
// portals in this help centre. Each row links to that portal's ticket detail.
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useHelpCentre } from './HelpCentreLayout';
import { StatusBadge } from '../../components/serviceDesk/ticketMeta';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function HelpCentreRequestsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { customer, customerChecked, portals = [] } = useHelpCentre();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map portal_id → subdomain so each ticket links into its own portal.
  const subById = useMemo(() => Object.fromEntries(portals.map(p => [p.portal_id, p.subdomain])), [portals]);
  const nameById = useMemo(() => Object.fromEntries(portals.map(p => [p.portal_id, p.name])), [portals]);

  useEffect(() => {
    if (!customerChecked) return;
    if (!customer) { navigate(`/helpcentre/${slug}/login`, { replace: true }); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/customer/tickets`, { withCredentials: true });
        setTickets(data || []);
      } catch { setTickets([]); }
      finally { setLoading(false); }
    })();
  }, [customer, customerChecked, slug, navigate]);

  const openTicket = (t) => {
    const sub = subById[t.portal_id];
    if (sub) navigate(`/help/${sub}/requests/${t.ticket_key}`);
  };

  if (loading) return <div className="text-sm text-zinc-400" data-testid="hc-requests-loading">Loading your requests…</div>;

  return (
    <div data-testid="hc-requests-page">
      <h1 className="text-xl font-bold text-zinc-900 font-['Outfit'] mb-4">My requests</h1>
      {tickets.length === 0 ? (
        <p className="text-sm text-zinc-500">You haven't raised any requests yet.</p>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">Key</th>
                <th className="text-left font-medium px-4 py-2.5">Summary</th>
                <th className="text-left font-medium px-4 py-2.5">Portal</th>
                <th className="text-left font-medium px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {tickets.map(t => (
                <tr key={t.ticket_id} onClick={() => openTicket(t)} className="hover:bg-zinc-50 cursor-pointer" data-testid={`hc-request-row-${t.ticket_key}`}>
                  <td className="px-4 py-3 font-medium text-blue-600 whitespace-nowrap">{t.ticket_key}</td>
                  <td className="px-4 py-3 text-zinc-800">{t.title}</td>
                  <td className="px-4 py-3 text-zinc-500 whitespace-nowrap">{nameById[t.portal_id] || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
